import { useEffect, useRef, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useLocation } from "react-router-dom";
import Table from "components/__new/common/Table";
import DownloadAsCSV from "components/__new/common/DownloadAsCSV";
import SortBy from "components/__new/common/SortBy";
import Print from "components/__new/common/Print";
import Pagination from "components/__new/common/pagination";
import SearchBox from "components/__new/common/SearchBox";
import Maybe from "components/__new/common/Maybe";
import DropdownNew from "utils/dropdown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { TransactionDetails } from "components/__new/payroll/runPayroll/TransactionDetails";
import { ReactComponent as IconEdit } from "assets/images/icons/project-icons/edit-pen.svg";
import { renderStatusColor as renderTransactionStatusColor } from "utils/statusColor";
import { TableContent } from "components/__new/common/TableContent";
import NumberFormat from "utils/numberFormat";
import CurrencyFormat from "utils/currencyFormat";
import useUrlQueryParams from "hooks/useUrlQueryParams";
import processParams from "utils/processParams";
import useUrlNavigation from "hooks/useUrlNavigation";
import * as payrollAction from "store/entities/payroll/runPayroll/action";


export const TransactionsListReadOnly = (props) => {
    const {payroll} = props;
    
    
    const {id} = useParams();
    const location = useLocation();
    const dispatch = useDispatch();
    const {urlQuery} = useUrlNavigation();
    const {urlQueryParams} = useUrlQueryParams();
    const { payrollTransactionsLoading, payrollTransactions, payrollTransactionsDownloadLoading, payrollTransactionsDownload } = useSelector((s) => s.entities.runPayroll);
    
    
    const downloadAsCSVRef = useRef();
    const printComponentRef = useRef();
    
    
    const [isOpenEmployeeDetails, setIsOpenEmployeeDetails] = useState(false);
    const [transaction, setTransaction] = useState({});
    const [listParams, setListParams] = useState({
        "page": 1,
        "limit": 20,
    });
    const [sortOptions, setSortOptions] = useState([
        {
            title: <>Date Created <FontAwesomeIcon icon="arrow-up" className="ml-2 mt-0.5" /></>,
            link: urlQuery("sort=createdAt&order=asc"),
            data: {sort: 'createdAt', order: 'asc'},
            active: false,
        },
        {
            title: <>Date Created <FontAwesomeIcon icon="arrow-down" className="ml-2 mt-0.5" /></>,
            link: urlQuery("sort=createdAt&order=desc"),
            data: {sort: 'createdAt', order: 'desc'},
            active: false,
        },
    ]);
    
        
    const paginationData = useMemo(() => {
        if (!payrollTransactions?.data) return {};
        
        const { data, ...paginationInfo } = payrollTransactions;
        return paginationInfo;
    }, [payrollTransactions])
        
    
    const getPayrollTransactions = () => {
        let params = {...listParams};

        params = processParams(urlQueryParams, params, "page", "number", 1, "page");
        params = processParams(urlQueryParams, params, "limit", "number", 20, "limit");
        params = processParams(urlQueryParams, params, "search", "string", "", "search");
        params = processParams(urlQueryParams, params, "sort", "string", "", "sortBy");
        params = processParams(urlQueryParams, params, "order", "string", "", "sortOrder");

        const sortValue = urlQueryParams["sort"];
        const orderValue = urlQueryParams["order"];
        if (sortValue && orderValue) {
            const newSortOptions = sortOptions?.map((sortOption) => {
                if (sortOption?.data?.sort === sortValue && sortOption?.data?.order === orderValue) {
                    return {...sortOption, active: true};
                }
                else {
                    return {...sortOption, active: false};
                }
            })
            setSortOptions(newSortOptions);
        }

        setListParams(params);
        dispatch(payrollAction.getPayrollTransactions(id, params));
    }
    
    
    const viewEmployeeDetails = (transaction) => {
        setTransaction(transaction);
        setIsOpenEmployeeDetails(true);
    }
    
    
    const transformDataDownload = (data) => {
        if (data?.length > 0){
            const transformedData = data.map((transaction) => {
                return ({
                    ...transaction,
                    salary_payout: transaction?.net_pay - transaction?.on_demand_pay - transaction?.manual_deductions + transaction?.manual_bonuses,
                })
            })
            return transformedData;
        }
        return data;
    }
    
    
    useEffect(() => {
        if (payroll?.id !== undefined){
            getPayrollTransactions();
        }
        // eslint-disable-next-line
    }, [payroll, location]);
    

    return (
        <>
            <TransactionDetails
                isOpen={isOpenEmployeeDetails}
                setIsOpen={setIsOpenEmployeeDetails}
                transaction={transaction}
            />
        
        
            <div className="box">
                <div className="flex flex-wrap justify-between">
                    <div className="max-w-sm flex-grow sm:mr-6">
                        <div className="h-10 flex lg:items-center">
                            <SearchBox placeholder="Search payees..." name="search" extraUrlParams="page=1" />
                        </div>
                    </div>
                    <div className="flex flex-wrap space-x-3 sm:space-x-6">
                        <div>
                            <SortBy options={sortOptions} title=" " firstOption="Sort by" />
                        </div>
                        
                        <div>
                            <DownloadAsCSV
                                ref={downloadAsCSVRef}
                                tooltip="Download payee list"
                                filename="Payee list.csv"
                                columns={[
                                    { label: "Employee name", key: "employee_name" },
                                    { label: "Employee number", key: "employee_number" },
                                    { label: "Workdays", key: "days_worked"},
                                    { label: "Net Pay", key: "net_pay" },
                                    { label: "Salary payout", key: "salary_payout" },
                                    { label: "Status", key: "status" },
                                ]}
                                click={() => {
                                    dispatch(payrollAction.downloadPayrollTransactions(id, {...listParams, page: 1, limit: 2000})).then(() => {
                                        downloadAsCSVRef.current?.downloadData();
                                    });
                                }}
                                loading={payrollTransactionsDownloadLoading}
                                response={transformDataDownload(payrollTransactionsDownload?.data)}
                            />
                        </div>

                        <div>
                            <Print component={printComponentRef} />
                        </div>
                    </div>
                </div>

                
                <div className="mt-6">
                    <div ref={printComponentRef}>

                        <div className="mb-4 text-xl hidden print:block">
                            Payee List
                        </div>
                        
                        <Table>
                            <Table.Head>
                                <th>Name</th>
                                <th>Workdays</th>
                                <th>Salary payout</th>
                                <th>Payslip</th>
                                <th>Status</th>
                                <Maybe condition={payroll?.type === "GROSS"}>
                                    <th></th>
                                </Maybe>
                            </Table.Head>
                            <Table.Body>

                                <TableContent
                                    loading={payrollTransactionsLoading}
                                    loadingText="Loading payroll transactions"
                                    data={payrollTransactions}
                                    colspan={10}
                                />

                                {payrollTransactionsLoading === false && payrollTransactions?.data?.map((payrollTransaction) =>
                                    <tr key={payrollTransaction?.id}>
                                        <td>
                                            <div className="font-medium capitalize">
                                                {payrollTransaction?.employee_name?.toLowerCase() || "-"}
                                            </div>
                                            <div className="text-fade">
                                                {payrollTransaction?.employee_number || "-"}
                                            </div>
                                        </td>
                                        <td className="text-fade-3">
                                            {NumberFormat(payrollTransaction?.days_worked, "no-kobo") || "-"}
                                        </td>
                                        <td className="text-fade-3">
                                            {CurrencyFormat(
                                                payrollTransaction?.net_pay 
                                                - payrollTransaction?.on_demand_pay 
                                                - payrollTransaction?.manual_deductions 
                                                + payrollTransaction?.manual_bonuses
                                            , "no-kobo") || "-"}
                                        </td>
                                        <td>
                                            <div className="w-max hover:underline cursor-pointer">
                                                View
                                            </div>
                                        </td>
                                        <td>
                                            {/* 
                                            <div className="label label-yellow">
                                                Payment Pending
                                            </div>
                                            */}
                                            <div className={"label capitalize " + renderTransactionStatusColor(payrollTransaction?.status)}>
                                                {payrollTransaction?.status}
                                            </div>
                                        </td>
                                        <Maybe condition={payroll?.type === "GROSS"}>
                                            <td>
                                                <DropdownNew>
                                                    <></>
                                                    <div>
                                                        <div onClick={() => viewEmployeeDetails(payrollTransaction)} className="dropdown-item dropdown-item-icon">
                                                            <IconEdit />
                                                            View Details
                                                        </div>
                                                    </div>
                                                </DropdownNew>
                                            </td>
                                        </Maybe>
                                    </tr>
                                )}

                            </Table.Body>
                        </Table>

                        <Maybe condition={payrollTransactionsLoading === false && payrollTransactions?.data?.length > 0}>
                            <div className="mt-6">
                                <Pagination data={paginationData} limit={paginationData?.limit} />
                            </div>
                        </Maybe>

                    </div>
                </div>
            </div>
        </>
    )
}